import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, AUTH_ACTION_TYPE, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { groupSwitchMap } from "@obg/rxjs";
import { merge, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { REWARD_ACTION_TYPE, RewardAction } from "./reward.action";
import { REWARD_MESSAGE_CODE, REWARD_MESSAGE_CONTEXT } from "./reward.const";
import { Reward, RewardFilter, RewardStateFilterType, RewardUpdatePayload } from "./reward.model";
import { RewardService } from "./reward.service";

/**
 * @deprecated [fabric-deprecated] - Use fabric RewardService from app-account instead
 */
@Injectable()
export class RewardEffect {
	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewards from app-account instead
	 */
	getRewards$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REWARD_ACTION_TYPE.getAll),
			map((x: ActionWithPayload<RewardStateFilterType[]>) => x.payload),
			groupSwitchMap((stateFilters) =>
				this.service.getAllByStates(stateFilters).pipe(
					map((response) =>
						this.action.getRewardsSuccess({
							rewards: response,
							stateFilters,
						})
					),
					catchError((error: AppError) => of(this.action.getRewardsFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetReward from app-account instead
	 */
	getReward$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REWARD_ACTION_TYPE.get),
			map((x: ActionWithPayload<RewardFilter>) => x.payload),
			groupSwitchMap((filter: RewardFilter) =>
				this.service.get(filter).pipe(
					map((reward) => this.action.getRewardSuccess(reward)),
					catchError((error: AppError) => of(this.action.getRewardFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService -AccountAppCommand.ClearRewards from app-account instead
	 */
	clearRewardsOnSessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => {
				return this.action.clearRewards();
			})
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService -AccountAppCommand.ClaimReward from app-account instead
	 */
	claimReward$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REWARD_ACTION_TYPE.claim),
			map((x: ActionWithPayload<RewardUpdatePayload>) => x.payload),
			groupSwitchMap((rewardPayload) =>
				this.service.claim(rewardPayload).pipe(
					map(() => this.action.claimRewardSuccess()),
					catchError((error: AppError) => of(this.action.claimRewardFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService -AccountAppCommand.ForfeitReward from app-account instead
	 */
	forfeitReward$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REWARD_ACTION_TYPE.forfeit),
			map((x: ActionWithPayload<Reward>) => x.payload),
			groupSwitchMap((reward) =>
				this.service.forfeit(reward).pipe(
					map(() => this.action.forfeitRewardSuccess()),
					catchError((error: AppError) => of(this.action.forfeitRewardFail(error)))
				)
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService -AccountAppCommand.ForfeitReward from app-account instead
	 */
	forfeitRewardSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REWARD_ACTION_TYPE.forfeitSuccess),
			map(() => this.action.getRewards())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[REWARD_ACTION_TYPE.claimFail]: REWARD_MESSAGE_CONTEXT.error,
				[REWARD_ACTION_TYPE.forfeitFail]: REWARD_MESSAGE_CONTEXT.error,
			}),
			this.messageService.map$({
				[REWARD_ACTION_TYPE.forfeitSuccess]: {
					context: REWARD_MESSAGE_CONTEXT.success,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: REWARD_MESSAGE_CODE.forfeitReward.success,
				},
				[REWARD_ACTION_TYPE.claimSuccess]: {
					context: REWARD_MESSAGE_CONTEXT.success,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: REWARD_MESSAGE_CODE.claimReward.success,
				},
				[REWARD_ACTION_TYPE.getFail]: {
					context: REWARD_MESSAGE_CONTEXT.getReward,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: REWARD_MESSAGE_CODE.reward.error,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: RewardAction,
		private service: RewardService,
		private messageService: MessageService
	) {}
}
